/** Eenmalige hint bij eerste focus op de hoofd-quick-add (#quick-add-input). */
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ONBOARDING_HINT_QUICK_ADD } from '@/constants/onboardingHints';
import { useAuth } from '@/features/auth/useAuth';
import { useTier } from '@/features/tier/useTier';
import {
  isDesktopBreakpoint,
  useBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { isOnboardingWelcomePlanTourPending } from '@/features/onboarding/utils/onboardingWelcomePlanSession';
import { markHintSeen } from '@/services/userService';

const WELCOME_PATH_RE = /^\/app\/(?:welcome|welkom)\/?$/;

/**
 * @returns {{
 *   showHint: boolean;
 *   onQuickAddFocus: () => void;
 *   onQuickAddBlur: () => void;
 *   onQuickAddChange: (value: string) => void;
 *   dismissHint: () => void;
 * }}
 */
export function useQuickAddFocusHint() {
  const breakpoint = useBreakpoint();
  const isDesktop = isDesktopBreakpoint(breakpoint);
  const location = useLocation();
  const { user } = useAuth();
  const { seenHints, profileLoaded } = useTier();
  const [showHint, setShowHint] = useState(false);
  const shownThisSessionRef = useRef(false);

  const isWelcomeRoute = WELCOME_PATH_RE.test(location.pathname);
  const planTourActive = isOnboardingWelcomePlanTourPending();
  const hintAlreadySeen = seenHints.includes(ONBOARDING_HINT_QUICK_ADD);

  const dismissHint = useCallback(() => {
    setShowHint(false);
  }, []);

  const onQuickAddFocus = useCallback(() => {
    if (shownThisSessionRef.current) {
      return;
    }
    if (
      !profileLoaded ||
      !user?.uid ||
      !isDesktop ||
      isWelcomeRoute ||
      planTourActive ||
      hintAlreadySeen
    ) {
      return;
    }
    shownThisSessionRef.current = true;
    setShowHint(true);
    void markHintSeen(user.uid, ONBOARDING_HINT_QUICK_ADD);
  }, [
    profileLoaded,
    user?.uid,
    isWelcomeRoute,
    planTourActive,
    hintAlreadySeen,
    isDesktop,
  ]);

  const onQuickAddBlur = useCallback(() => {
    if (showHint) {
      dismissHint();
    }
  }, [showHint, dismissHint]);

  const onQuickAddChange = useCallback(
    (value) => {
      if (showHint && value.length > 0) {
        dismissHint();
      }
    },
    [showHint, dismissHint],
  );

  return {
    showHint,
    onQuickAddFocus,
    onQuickAddBlur,
    onQuickAddChange,
    dismissHint,
  };
}
